import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { Status } from './tokens'
import './system.css'

const VISIBLE_MS = 4000

interface Message {
  id: number
  text: string
  status: Status | undefined
}

type Show = (text: string, status?: Status) => void

const ToastContext = createContext<Show | undefined>(undefined)

export function useToast(): Show {
  const show = useContext(ToastContext)

  if (!show) {
    throw new Error('useToast is only usable inside a ToastProvider')
  }

  return show
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<Message>()

  const show = useCallback<Show>((text, status) => {
    setMessage({ id: Date.now(), text, status })
  }, [])

  useEffect(() => {
    if (!message) {
      return
    }

    const timer = setTimeout(() => setMessage(undefined), VISIBLE_MS)

    return () => clearTimeout(timer)
  }, [message])

  const value = useMemo(() => show, [show])

  return (
    <ToastContext value={value}>
      {children}
      <div className="toast-region" aria-live="polite">
        {message ? (
          <p
            key={message.id}
            className={message.status ? `toast toast--${message.status}` : 'toast'}
          >
            {message.text}
          </p>
        ) : null}
      </div>
    </ToastContext>
  )
}
